import { Link } from 'react-router-dom';
import { Heart, Shield, Plane, Users, ChevronRight, MapPin, Check, MessageCircle, Sparkles, BadgeCheck, Clock, Star, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SEOHead from '../components/SEOHead';

export default function Landing() {
  const { isAuthenticated, user } = useAuth();
  const { t } = useTranslation();

  const stats = {
    members: "2,500+",
    citiesCovered: 12,
    newThisWeek: 47
  };

  const cities = ["Paris", "Zurich", "Luxembourg", "Brussels", "Lisbon", "Porto"];

  return (
    <div className="min-h-screen bg-[#FAF9F6] overflow-x-hidden">
      <SEOHead
        title="Portuguese Dating in Europe"
        description="More than dating. A connection to your roots, your culture, and the feeling of saudade. Meet verified Portuguese singles across Europe."
        path="/"
      />
      {/* Header */}
      <header className="fixed top-0 w-full z-50 backdrop-blur-xl bg-[#FAF9F6]/90 border-b border-[#E5E3DB]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-12 h-16 sm:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 group flex-shrink-0" data-testid="logo">
            <div className="relative">
              <Heart className="w-7 h-7 sm:w-9 sm:h-9 text-[#6E1A24] transition-transform group-hover:scale-110" strokeWidth={1.5} fill="currentColor" fillOpacity={0.1} />
            </div>
            <span className="text-xl sm:text-2xl font-serif font-medium text-[#2D2D2A] tracking-tight">LusoLove</span>
          </Link>
          
          <nav className="hidden md:flex items-center gap-10">
            <a href="#why" className="text-[#6C6C67] hover:text-[#6E1A24] transition-colors text-sm font-medium tracking-wide">{t('landing.nav_why')}</a>
            <a href="#how" className="text-[#6C6C67] hover:text-[#6E1A24] transition-colors text-sm font-medium tracking-wide">{t('landing.nav_how')}</a>
            <a href="#safety" className="text-[#6C6C67] hover:text-[#6E1A24] transition-colors text-sm font-medium tracking-wide">{t('landing.nav_safety')}</a>
          </nav>
          
          <div className="flex items-center gap-1.5 sm:gap-4">
            <div className="hidden sm:block"><LanguageSwitcher /></div>
            <div className="sm:hidden"><LanguageSwitcher variant="compact" /></div>
            {isAuthenticated ? (
              <Link to={user?.onboarding_complete ? "/discover" : "/onboarding"}>
                <Button className="bg-[#6E1A24] text-white rounded-full px-3 sm:px-6 py-2 sm:py-2.5 font-medium text-xs sm:text-sm hover:bg-[#8A2331] hover:shadow-lg hover:-translate-y-0.5 transition-all whitespace-nowrap" data-testid="enter-app-btn">
                  {t('landing.enter_app')}
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login" className="text-[#2D2D2A] hover:text-[#6E1A24] transition-colors hidden sm:block text-sm font-medium" data-testid="login-link">
                  {t('landing.sign_in')}
                </Link>
                <Link to="/register">
                  <Button className="bg-[#6E1A24] text-white rounded-full px-3 sm:px-6 py-2 sm:py-2.5 font-medium text-xs sm:text-sm hover:bg-[#8A2331] hover:shadow-lg hover:-translate-y-0.5 transition-all whitespace-nowrap" data-testid="get-started-btn">
                    {t('landing.create_profile')}
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-12 md:pt-44 md:pb-20 relative">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%236E1A24' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            {/* Left - Copy */}
            <div className="animate-fade-in max-w-xl">
              {/* Urgency Badge */}
              <div className="inline-flex items-center gap-3 bg-[#6E1A24]/5 border border-[#6E1A24]/10 rounded-full px-4 py-2 mb-6 sm:mb-8">
                <span className="w-2 h-2 rounded-full bg-[#2D6A4F] animate-pulse" />
                <span className="text-xs sm:text-sm text-[#6E1A24] font-medium">{t('landing.hero_badge', { count: stats.newThisWeek })}</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-serif font-light text-[#2D2D2A] leading-[1.1] mb-6 sm:mb-8 tracking-tight">
                {t('landing.hero_title_1')}<br />
                <span className="text-[#6E1A24] italic">{t('landing.hero_title_2')}</span><br />
                {t('landing.hero_title_3')}
              </h1>
              
              <p className="text-base sm:text-xl text-[#6C6C67] font-light leading-relaxed mb-6 sm:mb-8 max-w-md">
                {t('landing.hero_subtitle', { interpolation: { escapeValue: false } }).replace(/<1>/g, '').replace(/<\/1>/g, '').split('saudade').map((part, i, arr) => 
                  i < arr.length - 1 ? <span key={`saudade-${i}`}>{part}<span className="italic text-[#C97A63]">saudade</span></span> : <span key={`saudade-${i}`}>{part}</span>
                )}
              </p>
              
              {/* Main CTA */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8">
                <Link to="/register" className="group">
                  <Button className="bg-[#6E1A24] text-white rounded-full px-8 sm:px-12 py-4 sm:py-5 font-semibold text-base sm:text-lg hover:bg-[#8A2331] hover:shadow-2xl hover:-translate-y-1 transition-all shadow-xl shadow-[#6E1A24]/25 w-full sm:w-auto group-hover:scale-[1.02]" data-testid="hero-cta-btn">
                    {t('landing.hero_cta')}
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <a href="#how">
                  <Button className="bg-transparent text-[#2D2D2A] border-2 border-[#E5E3DB] rounded-full px-6 sm:px-8 py-4 sm:py-5 font-medium text-sm sm:text-base hover:border-[#6E1A24] hover:text-[#6E1A24] transition-all w-full sm:w-auto">
                    {t('landing.how_it_works')}
                  </Button>
                </a>
              </div>
              
              {/* Time indicator */}
              <div className="flex items-center gap-2 text-sm text-[#6C6C67] mb-10">
                <Clock className="w-4 h-4" />
                <span>{t('landing.time_hint')}</span>
              </div>
              
              {/* Trust Signals */}
              <div className="flex flex-wrap gap-x-6 gap-y-3">
                <div className="flex items-center gap-2 text-sm text-[#2D2D2A]">
                  <div className="w-5 h-5 rounded-full bg-[#2D6A4F] flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  </div>
                  <span className="font-medium">{t('landing.trust_verified')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-[#2D2D2A]">
                  <div className="w-5 h-5 rounded-full bg-[#2D6A4F] flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  </div>
                  <span className="font-medium">{t('landing.trust_no_bots')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-[#2D2D2A]">
                  <div className="w-5 h-5 rounded-full bg-[#2D6A4F] flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  </div>
                  <span className="font-medium">{t('landing.trust_serious')}</span>
                </div>
              </div>
            </div>
            
            {/* Right - Product Preview */}
            <div className="relative animate-fade-in lg:pl-8" style={{ animationDelay: '0.2s' }}>
              {/* Main Phone */}
              <div className="relative mx-auto w-[280px] sm:w-[320px]">
                <div className="relative bg-[#1a1a1a] rounded-[3rem] p-3 shadow-2xl shadow-black/20">
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 w-20 h-6 bg-black rounded-full" />
                  
                  <div className="bg-white rounded-[2.5rem] overflow-hidden">
                    <div className="relative h-[480px] sm:h-[540px]">
                      <img 
                        src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=600&h=800&fit=crop"
                        alt="Profile"
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                      
                      {/* Badges */}
                      <div className="absolute top-4 left-4 right-4 flex justify-between">
                        <div className="flex items-center gap-1.5 bg-white/95 backdrop-blur-sm rounded-full px-3 py-1.5 shadow-lg">
                          <BadgeCheck className="w-4 h-4 text-[#2D6A4F]" />
                          <span className="text-xs font-medium text-[#2D2D2A]">{t('common.verified')}</span>
                        </div>
                        <div className="flex items-center gap-1.5 bg-[#C97A63]/90 backdrop-blur-sm rounded-full px-3 py-1.5 shadow-lg">
                          <Plane className="w-4 h-4 text-white" />
                          <span className="text-xs font-medium text-white">In Lisbon</span>
                        </div>
                      </div>
                      
                      {/* Profile Info */}
                      <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                        <h3 className="text-2xl font-serif font-medium mb-1">Sofia, 28</h3>
                        <div className="flex items-center gap-1.5 text-white/80 text-sm mb-4">
                          <MapPin className="w-4 h-4" />
                          <span>Paris • From Porto</span>
                        </div>
                        <p className="text-white/70 text-sm line-clamp-2">
                          "Looking for someone who misses the sound of fado as much as I do..."
                        </p>
                      </div>
                    </div>
                    
                    {/* Swipe Buttons */}
                    <div className="bg-white px-6 py-4 flex justify-center gap-6">
                      <button className="w-14 h-14 rounded-full bg-[#F5F2EB] flex items-center justify-center hover:bg-[#E5E3DB] transition-colors">
                        <svg className="w-7 h-7 text-[#6C6C67]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                      <button className="w-14 h-14 rounded-full bg-[#6E1A24] flex items-center justify-center shadow-lg shadow-[#6E1A24]/30 hover:scale-105 transition-transform">
                        <Heart className="w-7 h-7 text-white" fill="white" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating Match Notification */}
              <div className="absolute -left-4 sm:-left-16 top-24 bg-white rounded-2xl p-4 shadow-xl shadow-black/10 border border-[#E5E3DB] hidden lg:block animate-slide-up" style={{ animationDelay: '0.5s' }}>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="w-12 h-12 rounded-full overflow-hidden ring-2 ring-[#6E1A24]">
                      <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop" alt="" className="w-full h-full object-cover" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#6E1A24] rounded-full flex items-center justify-center">
                      <Heart className="w-3 h-3 text-white" fill="white" />
                    </div>
                  </div>
                  <div>
                    <p className="font-medium text-[#2D2D2A] text-sm">{t('match_modal.title')}</p>
                    <p className="text-xs text-[#6C6C67]">{t('match_modal.subtitle', { name: 'Miguel' })}</p>
                  </div>
                </div>
              </div>
              
              {/* Floating Chat Preview */}
              <div className="absolute -right-4 sm:-right-12 bottom-32 bg-white rounded-2xl p-4 shadow-xl shadow-black/10 border border-[#E5E3DB] max-w-[200px] hidden lg:block animate-slide-up" style={{ animationDelay: '0.7s' }}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full overflow-hidden">
                    <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop" alt="" className="w-full h-full object-cover" />
                  </div>
                  <span className="text-sm font-medium text-[#2D2D2A]">Maria</span>
                </div>
                <div className="bg-[#F5F2EB] rounded-2xl rounded-bl-sm px-3 py-2 mb-2">
                  <p className="text-sm text-[#2D2D2A]">Também tens saudades de Portugal? 🇵🇹</p>
                </div>
                <div className="flex items-center gap-1 text-[#6C6C67]">
                  <MessageCircle className="w-3 h-3" />
                  <span className="text-xs">{t('chat.type_message')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Bar */}
      <section className="py-10 border-y border-[#E5E3DB] bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-4">
              <div className="flex -space-x-3">
                {[
                  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=60&h=60&fit=crop",
                  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=60&h=60&fit=crop",
                  "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=60&h=60&fit=crop",
                  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=60&h=60&fit=crop",
                  "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=60&h=60&fit=crop"
                ].map((src) => (
                  <div key={src} className="w-10 h-10 rounded-full border-2 border-white overflow-hidden shadow-sm">
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
                <div className="w-10 h-10 rounded-full border-2 border-white bg-[#6E1A24] flex items-center justify-center text-white text-xs font-medium shadow-sm">
                  +{stats.newThisWeek}
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-[#2D2D2A]">{t('landing.social_join', { count: stats.members })}</p>
                <p className="text-xs text-[#6C6C67]">{t('landing.social_finding')}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-8 md:gap-12">
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-serif font-light text-[#6E1A24]">5</p>
                <p className="text-xs text-[#6C6C67]">{t('landing.stats_countries')}</p>
              </div>
              <div className="w-px h-10 bg-[#E5E3DB]" />
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-serif font-light text-[#6E1A24]">{stats.citiesCovered}</p>
                <p className="text-xs text-[#6C6C67]">{t('landing.stats_cities')}</p>
              </div>
              <div className="w-px h-10 bg-[#E5E3DB]" />
              <div className="text-center">
                <p className="text-2xl sm:text-3xl font-serif font-light text-[#6E1A24]">100%</p>
                <p className="text-xs text-[#6C6C67]">{t('landing.stats_portuguese')}</p>
              </div>
            </div>
          </div>
          
          <div className="mt-6 pt-6 border-t border-[#E5E3DB]/50">
            <p className="text-center text-sm text-[#6C6C67]">
              {t('landing.cities_active')} <span className="text-[#2D2D2A] font-medium">{cities.join(" • ")}</span>
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how" className="py-24 md:py-32 bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-12">
          <div className="text-center mb-16">
            <p className="text-sm font-medium tracking-[0.2em] text-[#C97A63] uppercase mb-4">{t('landing.how_label')}</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-light text-[#2D2D2A] mb-4">
              {t('landing.how_section_title')}
            </h2>
            <p className="text-lg text-[#6C6C67] font-light">
              {t('landing.how_section_subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            <div className="relative text-center group">
              <div className="relative inline-block mb-8">
                <div className="w-20 h-20 rounded-3xl bg-[#6E1A24]/10 flex items-center justify-center group-hover:bg-[#6E1A24]/20 transition-colors">
                  <Users className="w-10 h-10 text-[#6E1A24]" strokeWidth={1.5} />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[#6E1A24] text-white flex items-center justify-center font-serif font-medium">1</div>
              </div>
              <h3 className="text-xl font-serif font-medium text-[#2D2D2A] mb-3">{t('landing.how_step1_title')}</h3>
              <p className="text-[#6C6C67] font-light leading-relaxed">{t('landing.how_step1_desc')}</p>
              <div className="hidden md:block absolute top-10 -right-6 lg:-right-8">
                <ArrowRight className="w-6 h-6 text-[#E5E3DB]" />
              </div>
            </div>
            
            <div className="relative text-center group">
              <div className="relative inline-block mb-8">
                <div className="w-20 h-20 rounded-3xl bg-[#C97A63]/10 flex items-center justify-center group-hover:bg-[#C97A63]/20 transition-colors">
                  <Heart className="w-10 h-10 text-[#C97A63]" strokeWidth={1.5} />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[#6E1A24] text-white flex items-center justify-center font-serif font-medium">2</div>
              </div>
              <h3 className="text-xl font-serif font-medium text-[#2D2D2A] mb-3">{t('landing.how_step2_title')}</h3>
              <p className="text-[#6C6C67] font-light leading-relaxed">{t('landing.how_step2_desc')}</p>
              <div className="hidden md:block absolute top-10 -right-6 lg:-right-8">
                <ArrowRight className="w-6 h-6 text-[#E5E3DB]" />
              </div>
            </div>
            
            <div className="relative text-center group">
              <div className="relative inline-block mb-8">
                <div className="w-20 h-20 rounded-3xl bg-[#E6D5B8]/50 flex items-center justify-center group-hover:bg-[#E6D5B8] transition-colors">
                  <MessageCircle className="w-10 h-10 text-[#6E1A24]" strokeWidth={1.5} />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-[#6E1A24] text-white flex items-center justify-center font-serif font-medium">3</div>
              </div>
              <h3 className="text-xl font-serif font-medium text-[#2D2D2A] mb-3">{t('landing.how_step3_title')}</h3>
              <p className="text-[#6C6C67] font-light leading-relaxed">{t('landing.how_step3_desc')}</p>
            </div>
          </div>
          
          <div className="mt-16 text-center">
            <Link to="/register" className="group inline-block">
              <Button className="bg-[#6E1A24] text-white rounded-full px-10 py-4 font-semibold text-base hover:bg-[#8A2331] hover:shadow-xl hover:-translate-y-1 transition-all shadow-lg shadow-[#6E1A24]/20" data-testid="how-cta-btn">
                {t('landing.how_cta')}
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <p className="mt-4 text-sm text-[#6C6C67]">{t('landing.how_cta_free')}</p>
          </div>
        </div>
      </section>

      {/* Why LusoLove */}
      <section id="why" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-12">
          <div className="text-center mb-20">
            <p className="text-sm font-medium tracking-[0.2em] text-[#C97A63] uppercase mb-4">{t('landing.why_label')}</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-light text-[#2D2D2A] mb-6">
              {t('landing.why_title')}
            </h2>
            <p className="text-lg text-[#6C6C67] max-w-2xl mx-auto font-light">
              {t('landing.why_subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            <div className="group relative bg-[#FAF9F6] rounded-3xl p-10 border border-[#E5E3DB] hover:border-[#6E1A24]/30 hover:shadow-xl hover:shadow-[#6E1A24]/5 transition-all duration-300">
              <div className="w-16 h-16 rounded-2xl bg-[#6E1A24]/10 flex items-center justify-center mb-8 group-hover:bg-[#6E1A24]/20 transition-colors">
                <Users className="w-8 h-8 text-[#6E1A24]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-serif font-medium text-[#2D2D2A] mb-4">{t('landing.why_feature1_title')}</h3>
              <p className="text-[#6C6C67] font-light leading-relaxed">{t('landing.why_feature1_desc')}</p>
            </div>
            
            <div className="group relative bg-[#FAF9F6] rounded-3xl p-10 border border-[#E5E3DB] hover:border-[#C97A63]/30 hover:shadow-xl hover:shadow-[#C97A63]/5 transition-all duration-300">
              <div className="w-16 h-16 rounded-2xl bg-[#C97A63]/10 flex items-center justify-center mb-8 group-hover:bg-[#C97A63]/20 transition-colors">
                <Plane className="w-8 h-8 text-[#C97A63]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-serif font-medium text-[#2D2D2A] mb-4">{t('landing.why_feature2_title')}</h3>
              <p className="text-[#6C6C67] font-light leading-relaxed">{t('landing.why_feature2_desc')}</p>
            </div>
            
            <div className="group relative bg-[#FAF9F6] rounded-3xl p-10 border border-[#E5E3DB] hover:border-[#E6D5B8] hover:shadow-xl hover:shadow-[#E6D5B8]/20 transition-all duration-300">
              <div className="w-16 h-16 rounded-2xl bg-[#E6D5B8]/50 flex items-center justify-center mb-8 group-hover:bg-[#E6D5B8] transition-colors">
                <Shield className="w-8 h-8 text-[#6E1A24]" strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-serif font-medium text-[#2D2D2A] mb-4">{t('landing.why_feature3_title')}</h3>
              <p className="text-[#6C6C67] font-light leading-relaxed">{t('landing.why_feature3_desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 md:py-32 bg-[#F5F2EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-12">
          <div className="text-center mb-16">
            <p className="text-sm font-medium tracking-[0.2em] text-[#C97A63] uppercase mb-4">{t('landing.testimonials_label')}</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-light text-[#2D2D2A]">
              {t('landing.testimonials_title')}
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-[#E5E3DB]">
              <div className="flex items-center gap-1 mb-4">
                {[1,2,3,4,5].map(i => (<Star key={i} className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" />))}
              </div>
              <p className="text-[#2D2D2A] font-light leading-relaxed mb-6">"{t('landing.testimonial1')}"</p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full overflow-hidden">
                  <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop" alt="" className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="font-medium text-[#2D2D2A]">Sofia M.</p>
                  <p className="text-sm text-[#6C6C67]">Paris, France</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-[#E5E3DB]">
              <div className="flex items-center gap-1 mb-4">
                {[1,2,3,4,5].map(i => (<Star key={i} className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" />))}
              </div>
              <p className="text-[#2D2D2A] font-light leading-relaxed mb-6">"{t('landing.testimonial2')}"</p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full overflow-hidden">
                  <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop" alt="" className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="font-medium text-[#2D2D2A]">Miguel R.</p>
                  <p className="text-sm text-[#6C6C67]">Zurich, Switzerland</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-3xl p-8 shadow-sm border border-[#E5E3DB]">
              <div className="flex items-center gap-1 mb-4">
                {[1,2,3,4,5].map(i => (<Star key={i} className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" />))}
              </div>
              <p className="text-[#2D2D2A] font-light leading-relaxed mb-6">"{t('landing.testimonial3')}"</p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full overflow-hidden">
                  <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop" alt="" className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="font-medium text-[#2D2D2A]">Inês C.</p>
                  <p className="text-sm text-[#6C6C67]">Luxembourg</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Safety Section */}
      <section id="safety" className="py-24 md:py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 text-center">
          <p className="text-sm font-medium tracking-[0.2em] text-[#C97A63] uppercase mb-4">{t('landing.safety_label')}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-light text-[#2D2D2A] mb-6">
            {t('landing.safety_title')}
          </h2>
          <p className="text-lg text-[#6C6C67] max-w-2xl mx-auto font-light mb-16">
            {t('landing.safety_subtitle')}
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            <div className="group">
              <div className="w-16 h-16 rounded-2xl bg-[#2D6A4F]/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-[#2D6A4F]/20 transition-colors">
                <BadgeCheck className="w-8 h-8 text-[#2D6A4F]" strokeWidth={1.5} />
              </div>
              <h3 className="font-medium text-[#2D2D2A] mb-2">{t('landing.safety_verification')}</h3>
              <p className="text-sm text-[#6C6C67] font-light">{t('landing.safety_verification_desc')}</p>
            </div>
            
            <div className="group">
              <div className="w-16 h-16 rounded-2xl bg-[#6E1A24]/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-[#6E1A24]/20 transition-colors">
                <Shield className="w-8 h-8 text-[#6E1A24]" strokeWidth={1.5} />
              </div>
              <h3 className="font-medium text-[#2D2D2A] mb-2">{t('landing.safety_report')}</h3>
              <p className="text-sm text-[#6C6C67] font-light">{t('landing.safety_report_desc')}</p>
            </div>
            
            <div className="group">
              <div className="w-16 h-16 rounded-2xl bg-[#C97A63]/10 flex items-center justify-center mx-auto mb-6 group-hover:bg-[#C97A63]/20 transition-colors">
                <Sparkles className="w-8 h-8 text-[#C97A63]" strokeWidth={1.5} />
              </div>
              <h3 className="font-medium text-[#2D2D2A] mb-2">{t('landing.safety_adults')}</h3>
              <p className="text-sm text-[#6C6C67] font-light">{t('landing.safety_adults_desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-[#6E1A24]" />
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M50 50m-40 0a40,40 0 1,0 80,0a40,40 0 1,0 -80,0' fill='none' stroke='white' stroke-width='1'/%3E%3C/svg%3E")`,
          backgroundSize: '100px 100px'
        }} />
        
        <div className="max-w-4xl mx-auto px-6 sm:px-12 relative z-10 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-6xl font-serif font-light text-white mb-6 leading-tight">
            {t('landing.final_cta_title')}
          </h2>
          <p className="text-xl text-white/80 font-light mb-10 max-w-xl mx-auto">
            {t('landing.final_cta_subtitle')}
          </p>
          <Link to="/register" className="group inline-block">
            <Button className="bg-white text-[#6E1A24] hover:bg-[#FAF9F6] rounded-full px-14 py-6 font-semibold text-lg shadow-2xl hover:-translate-y-1 transition-all group-hover:scale-[1.02]" data-testid="cta-join-btn">
              {t('landing.final_cta_btn')}
              <ArrowRight className="w-6 h-6 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
          
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-white/70 text-sm">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4" />
              <span>{t('landing.cta_free')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4" />
              <span>{t('landing.cta_2min')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4" />
              <span>{t('landing.cta_no_card')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* SEO: Portuguese Communities in Europe */}
      <section className="py-14 bg-[#F5F2EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-12">
          <h2 className="text-xl font-serif font-medium text-[#2D2D2A] mb-6 text-center">Portuguese dating across Europe</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {[
              { slug: 'portuguese-dating-france', label: 'France', flag: '\u{1F1EB}\u{1F1F7}' },
              { slug: 'portuguese-dating-switzerland', label: 'Switzerland', flag: '\u{1F1E8}\u{1F1ED}' },
              { slug: 'portuguese-dating-luxembourg', label: 'Luxembourg', flag: '\u{1F1F1}\u{1F1FA}' },
              { slug: 'portuguese-dating-germany', label: 'Germany', flag: '\u{1F1E9}\u{1F1EA}' },
              { slug: 'portuguese-dating-belgium', label: 'Belgium', flag: '\u{1F1E7}\u{1F1EA}' },
              { slug: 'portuguese-dating-paris', label: 'Paris', flag: '\u{1F1EB}\u{1F1F7}' },
              { slug: 'portuguese-dating-geneva', label: 'Geneva', flag: '\u{1F1E8}\u{1F1ED}' },
              { slug: 'portuguese-dating-zurich', label: 'Zurich', flag: '\u{1F1E8}\u{1F1ED}' },
              { slug: 'portuguese-dating-brussels', label: 'Brussels', flag: '\u{1F1E7}\u{1F1EA}' },
            ].map(page => (
              <Link
                key={page.slug}
                to={`/${page.slug}`}
                className="flex items-center gap-2 bg-white rounded-xl border border-[#E5E3DB] px-4 py-3 hover:border-[#6E1A24]/30 hover:shadow-sm transition-all text-sm font-medium text-[#2D2D2A]"
                data-testid={`seo-footer-${page.slug}`}
              >
                <span>{page.flag}</span>
                <span className="truncate">{page.label}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 bg-[#2D2D2A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-12">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="flex items-center gap-3">
              <Heart className="w-7 h-7 text-[#C97A63]" strokeWidth={1.5} fill="currentColor" fillOpacity={0.3} />
              <span className="text-2xl font-serif">LusoLove</span>
            </div>
            
            <div className="flex flex-wrap gap-x-6 sm:gap-x-10 gap-y-2 text-sm text-white/60">
              <Link to="/faq" className="hover:text-white transition-colors" data-testid="footer-faq">{t('nav.faq', 'FAQ')}</Link>
              <Link to="/contact" className="hover:text-white transition-colors" data-testid="footer-contact">{t('landing.footer_contact')}</Link>
              <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">{t('landing.footer_privacy', 'Privacy Policy')}</Link>
              <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms">{t('landing.footer_terms', 'Terms of Use')}</Link>
              <Link to="/refund" className="hover:text-white transition-colors" data-testid="footer-refund">{t('landing.footer_refund', 'Refund Policy')}</Link>
            </div>
            
            <p className="text-sm text-white/40">
              {t('landing.footer_tagline')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
